import * as _ from 'lodash';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';

import { useAddToast } from '../../../contexts/ToastContext';
import useAsyncValue from '../../../hooks/useAsyncValue';
import useRequestConnectedToAsyncValue from '../../../hooks/useRequestConnectedToAsyncValue';
import { put } from '../../../redux/api';
import { nuc } from '../../../utils';
//  Note that we are intentionally using this Project type, since it includes the associations that we need.
import type { Project } from '../../../types';

import { convertGeneralSettingsToProject, pickGeneralSettingsFromProject } from './general/types';
import type { ProjectGeneralSettings } from './general/types';
import { pickIssueTrackerSettingsFromProject } from './issue-tracker/types';
import type { ProjectIssueTrackerSettings } from './issue-tracker/types';
import { convertNotificationsSettingsToProject, pickNotificationsSettingsFromProject } from './notifications/types';
import type { ProjectNotificationsSettings } from './notifications/types';
import { useCanUserEditProject } from './permissions';
import { pickIssueSettingsFromProject } from './policies/types';
import type { ProjectIssueSettings } from './policies/types';
import { pickReportsSettingsFromProject } from './reports/types';
import type { ProjectReportsSettings } from './reports/types';
import { convertUpdateHooksSettingsToProject, pickUpdateHooksSettingsFromProject } from './update-hooks/types';
import type { ProjectUpdateHooksSave, ProjectUpdateHooksSettings } from './update-hooks/types';

//  Helper for checking the permissions and building the base URL for the project settings page.
//  This is broken off because it is used below in useSetupProjectSettingsContextForPage but is also used for `builds` which does not use that hook.
const useEditPermissionsAndProjectBaseURL = (locator: string) => {
  //  Check if the user has permission to edit the settings.
  const canUserEditSettings = useCanUserEditProject(locator);
  //  Stores the base URL for the project settings page,
  //  which is used to redirect back to the base settings page if users don't have permission to edit.
  const projectSettingsBaseURL = useMemo(() => `/projects/${nuc(locator)}/settings`, [locator]);
  return {
    canUserEditSettings,
    projectSettingsBaseURL,
  };
};

type AnyProjectSettingsPage =
| ProjectGeneralSettings
| ProjectIssueSettings
| ProjectIssueTrackerSettings
| ProjectNotificationsSettings
| ProjectReportsSettings
| ProjectUpdateHooksSettings;

//  This generic takes in one of the project settings "pages" and includes
//  the handlers and other generic booleans which are common across all pages.
export type SettingsPageDataAndHandlers<S> = {
  settings: S;
  //  A copy of the locator in case it is needed. Useful for generating link URLs within this project.
  locator: string;
  //  Lets us know if the data has been changed since the page was mounted
  isDirty: boolean;
  //  Lets us know if the data is currently being saved
  isSaving: boolean;
  //  This method is used to update the local copy of the data as the user edits it.
  onUpdate: (data: Partial<S>) => void;
  //  This is used to actually persist a save to the backend.
  onSave: () => Promise<unknown>;
  //  Array of error messages, collected from any failed save requests
  errors?: string[] | null;
  //  Checks that the user has permission to edit the settings.
  canUserEditSettings: boolean;
  //  The base URL for the project settings page, which is used to redirect back to the base settings page if users don't have permission to edit.
  projectSettingsBaseURL: string;
};

export type ProjectSettingsContextType = {
  general: SettingsPageDataAndHandlers<ProjectGeneralSettings>;
  issues: SettingsPageDataAndHandlers<ProjectIssueSettings>;
  issueTracker: SettingsPageDataAndHandlers<ProjectIssueTrackerSettings>;
  notifications: SettingsPageDataAndHandlers<ProjectNotificationsSettings>;
  reports: SettingsPageDataAndHandlers<ProjectReportsSettings>;
  updateHooks: SettingsPageDataAndHandlers<ProjectUpdateHooksSettings>;
  //  Builds aren't editable in the way that other settings pages are.
  builds: Pick<Project, 'locator' | 'vcs_host'> & Pick<SettingsPageDataAndHandlers<void>, 'canUserEditSettings' | 'projectSettingsBaseURL'>;
  // The project object from the backend. This represents the up-to-date state of the project.
  project: Project;
};

type ProjectSettingsSave = Partial<Project> | ProjectUpdateHooksSave;

//  This method is optionally passed to the `useSetupProjectSettingsContextForPage` hook
//  to convert the settings back into a Partial<Project> object to be saved.
type ConvertSettingsToProject<S extends AnyProjectSettingsPage> = (settings: S) => ProjectSettingsSave;

/**
 * The top-level project settings gets a `project` prop which is fetched
 * at the top-level ProjectContainer. This is passed directly to our
 * ProjectIssuesSettingsContainer (as it is a Route component).
 *
 * Instead of trying to drill/cloneElement to provide `project` to each of the
 * project settings pages, we use this context which limits the amount of
 * data being passed down to each page.
 *
 * It also manages the "copy" of the state for each page, and the handlers
 * for updating and saving the data.
 */
export const ProjectSettingsContext = createContext<ProjectSettingsContextType>({} as ProjectSettingsContextType);

//  Top-level hook to consume the entire project settings context.
//  This is used by each of the hooks that power the individual pages.
export const useProjectSettingsContext = () => useContext(ProjectSettingsContext);

type UseSetupProjectSettingsContextForPageProps<S extends AnyProjectSettingsPage> = {
  locator: string,
  initialState: S,
  convertSettingsToProject?: ConvertSettingsToProject<S>,
  onReloadProject: () => void,
};

/**
 * Generic hook to setup the context for a specific project settings page.
 * This is used in the ProjectSettingsContextProvider to set up each page.
 */
function useSetupProjectSettingsContextForPage<S extends AnyProjectSettingsPage>({
  locator,
  initialState,
  convertSettingsToProject,
  onReloadProject,
}: UseSetupProjectSettingsContextForPageProps<S>): SettingsPageDataAndHandlers<S> {
  const onAddToast = useAddToast();
  const [settings, setSettings] = useState<S>(initialState);
  const [isDirty, setIsDirty] = useState(false);

  //  Handle updates
  const onUpdate = useCallback((data: Partial<S>) =>
    setSettings((prev) => {
      const newSettings = { ...prev, ...data };
      setIsDirty(!_.isEqual(initialState, newSettings));
      return newSettings;
    }), [initialState]);

  //  Handle saving
  const onSaveAsyncValue = useAsyncValue<null>();
  const saveProjectRequest = useCallback(async () => {
    //  If we have a custom converter, use it, otherwise just use the settings as-is.
    const project = convertSettingsToProject?.(settings) ?? (settings as Partial<Project>);
    try {
      await put<ProjectSettingsSave, never>(`/api/projects/${nuc(locator)}`, project);
      setIsDirty(false);
      //  Add a generic success message.
      onAddToast({ type: 'success', content: 'Project settings saved.' });
      //  This calls all the way up to the ProjectContainer to refetch the project.
      //  This is required as this will refresh the data in the Reflux store, which
      //  is used on other pages in within the ProjectContainer, and we want them to have
      //  the most up-to-date data.
      onReloadProject();
    } catch (error) {
      //  Add a generic failed message.
      onAddToast({ type: 'error', content: 'Project settings could not be saved.' });
      //  We want useAsyncRequest to capture this error and populate the errors array.
      throw error;
    }
    //  NOTE: this is to appease TS, as our useAsyncRequest system is expecting that there will be `data` in the response.
    return { data: null };
  }, [locator, settings, convertSettingsToProject, onAddToast, onReloadProject]);
  const onSave = useRequestConnectedToAsyncValue(
    saveProjectRequest,
    onSaveAsyncValue,
  );
  const { canUserEditSettings, projectSettingsBaseURL } = useEditPermissionsAndProjectBaseURL(locator);

  return {
    locator,
    settings,
    isDirty,
    isSaving: onSaveAsyncValue.isLoading,
    errors: onSaveAsyncValue.errors,
    onSave,
    onUpdate,
    canUserEditSettings,
    projectSettingsBaseURL,
  };
}

//  The builds page needs much less data + no handlers/state, so we don't need to use the generic hook above.
const useSetupBuildSettingsContext = (locator: string, vcsHost: Project['vcs_host']) => {
  const { canUserEditSettings, projectSettingsBaseURL } = useEditPermissionsAndProjectBaseURL(locator);
  return useMemo(() => ({
    locator,
    vcs_host: vcsHost,
    canUserEditSettings,
    projectSettingsBaseURL,
  }), [locator, vcsHost, canUserEditSettings, projectSettingsBaseURL]);
};

type ProjectSettingsContextProviderProps = {
  project: Project;
  onReloadProject: () => void;
};

/**
 * Top-level project settings context provider.
 * This wraps everything and provides the context for each individual page.
 */
const ProjectSettingsContextProvider: FunctionComponent<ProjectSettingsContextProviderProps> = ({
  children,
  project,
  onReloadProject,
}) => {
  const locator = project?.locator as string;

  const general = useSetupProjectSettingsContextForPage<ProjectGeneralSettings>({
    locator,
    onReloadProject,
    initialState: pickGeneralSettingsFromProject(project),
    convertSettingsToProject: convertGeneralSettingsToProject,
  });

  const issues = useSetupProjectSettingsContextForPage<ProjectIssueSettings>({
    locator,
    onReloadProject,
    initialState: pickIssueSettingsFromProject(project),
  });

  const issueTracker = useSetupProjectSettingsContextForPage<ProjectIssueTrackerSettings>({
    locator,
    onReloadProject,
    initialState: pickIssueTrackerSettingsFromProject(project),
  });

  const notifications = useSetupProjectSettingsContextForPage<ProjectNotificationsSettings>({
    locator,
    onReloadProject,
    initialState: pickNotificationsSettingsFromProject(project),
    convertSettingsToProject: convertNotificationsSettingsToProject,
  });

  const reports = useSetupProjectSettingsContextForPage<ProjectReportsSettings>({
    locator,
    onReloadProject,
    initialState: pickReportsSettingsFromProject(project),
  });

  const updateHooks = useSetupProjectSettingsContextForPage<ProjectUpdateHooksSettings>({
    locator,
    onReloadProject,
    initialState: pickUpdateHooksSettingsFromProject(project),
    convertSettingsToProject: convertUpdateHooksSettingsToProject,
  });

  const builds: ProjectSettingsContextType['builds'] = useSetupBuildSettingsContext(locator, project.vcs_host);

  const contextValue: ProjectSettingsContextType = useMemo(() => ({
    general,
    issues,
    issueTracker,
    notifications,
    reports,
    updateHooks,
    builds,
    project,
  }), [general, issues, issueTracker, notifications, reports, updateHooks, builds, project]);

  return (
    <ProjectSettingsContext.Provider value={contextValue}>
      {children}
    </ProjectSettingsContext.Provider>
  );
};

export default ProjectSettingsContextProvider;

//  This is only exported for testing purposes and is not intended to be consumed outside of this file.
export const testImplementations = {
  useSetupProjectSettingsContextForPage,
};
